/**
 * "Thu hồi" step two — Task T2-D. Names the subject and the code being revoked,
 * the same discipline `RevokeConfirm.tsx` (WP-K5, devices) states for its own
 * inventory: "are you sure?" transfers no information; naming exactly what is
 * about to change does.
 *
 * Lighter than `RevokeConfirm`'s own gate (no reason, no handle typed back) because
 * revoking a permission grant is reversible by re-granting it — unlike a device
 * revoke, which is terminal — and the row itself states as much: it moves to "Đã
 * thu hồi" and stays on screen for audit rather than disappearing.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../../i18n';
import type { PermissionGrant } from '../../../api/permission-grants';
import type { WorkforceApiError } from '../../../api/errors';
import { ApiErrorNote } from '../../../states/ApiErrorNote';
import { StatusNote } from '../../../states/StatusNote';

export interface RevokeGrantPanelProps {
  readonly grant: PermissionGrant;
  /** The same "{type}: {value}" text the table's own subject cell renders. */
  readonly subjectLabel: string;
  readonly pending: boolean;
  readonly error: WorkforceApiError | null;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export function RevokeGrantPanel({
  grant,
  subjectLabel,
  pending,
  error,
  onConfirm,
  onCancel,
}: RevokeGrantPanelProps): ReactNode {
  const { t } = useI18n();
  return (
    <StatusNote
      severity="high"
      role="alert"
      title={t('adminPermissions.revoke.title', {
        code: grant.permission_code,
        subject: subjectLabel,
      })}
      testId="t2d-revoke"
      actions={
        <>
          <button
            type="button"
            className="wp-button wp-button--danger"
            data-testid="t2d-revoke-confirm"
            disabled={pending}
            onClick={onConfirm}
          >
            {pending
              ? t('adminPermissions.revoke.confirming')
              : t('adminPermissions.revoke.confirm')}
          </button>
          <button
            type="button"
            className="wp-button wp-button--quiet"
            data-testid="t2d-revoke-cancel"
            onClick={onCancel}
          >
            {t('adminPermissions.revoke.cancel')}
          </button>
        </>
      }
    >
      <p>{t('adminPermissions.revoke.body')}</p>
      {error === null ? null : (
        <ApiErrorNote
          error={error}
          subject={t('adminPermissions.subject')}
          testId="t2d-revoke-error"
        />
      )}
    </StatusNote>
  );
}
